import React, { useState, useEffect, useRef } from 'react';
import { properties } from '../../properties';
import apiFetch from '../../auth/apiFetch';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import {
    getGymsAffiliate,
    getGymResponsibles,
    getStates,
    getTeachersSummary,
} from '../../view/HTTPService';
import { Toast } from 'primereact/toast';
import { isValidE164, formatPhoneForDisplay, e164ToMasked, stripMask } from '../../utils/phone';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputMask } from 'primereact/inputmask';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { Fieldset } from 'primereact/fieldset';
import { useTranslation } from 'react-i18next';

import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';

import { GymFormDialog } from './GymFormDialog';

const emptyGym = {
    id: '',
    jkaId: '',
    name: '',
    tradeName: '',
    localDate: '',
    cnpj: '',
    affiliate: false,
    state: { id: '', name: '' },
    status: false,
    gymAddress: {
        addressComplement: '', city: '', neighborhood: '', state: '',
        street: '', streetNumber: '', zipCode: '',
    },
    contacts: {
        email1: '', email2: '', email3: '',
        mobile1: '', mobile2: '', phone1: '', phone2: '',
    },
    socialMedia: {
        facebookId: '', instagramId: '', twitterId: '', tiktokId: '', site: '',
    },
    gymLicenseList: [],
    annuityList: [],
    technicalResponsibles: [],
    gymResponsibles: [],
};

/**
 * GymList - lista de dojos com dialog decomposto em 4 tabs.
 *
 * Props (todas opcionais):
 *   titleKey       - chave i18n do titulo (default: 'msg.jkaGymsAffiliate')
 *   fetchGyms      - fn async pra carregar a lista inicial (default: getGymsAffiliate).
 *                    Passe null pra NAO carregar nada.
 *   filterByState  - se passado (fn), mostra dropdown de estado no header.
 */
function GymList({
    titleKey = 'msg.jkaGymsAffiliate',
    fetchGyms = getGymsAffiliate,
    filterByState = null,
} = {}) {
    const { t } = useTranslation();

    // ==================== DADOS PRINCIPAIS ====================
    const [gyms, setGyms] = useState(null);
    const [gym, setGym] = useState(emptyGym);
    const [states, setStates] = useState(null);
    const [gymResponsibles, setGymResponsibles] = useState(null);
    const [teachers, setTeachers] = useState(null);

    // ==================== UI ====================
    const [activeIndex, setActiveIndex] = useState(0);
    const [gymDialog, setGymDialog] = useState(false);
    const [deleteGymDialog, setDeleteGymDialog] = useState(false);
    const [deleteGymsDialog, setDeleteGymsDialog] = useState(false);
    const [selectedGyms, setSelectedGyms] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [globalFilter, setGlobalFilter] = useState(null);
    const [idToDelete, setIdToDelete] = useState(0);
    const [gymApiError, setGymApiError] = useState('');
    const toast = useRef(null);
    const dt = useRef(null);

    // Dropdown/checkbox
    const [selectedState, setSelectedState] = useState(null);
    const [checked, setChecked] = useState(false);

    // Filtro de estado no header
    const [selectedFilterState, setSelectedFilterState] = useState(null);

    // Responsibles
    const [responsiblesDialog, setResponsiblesDialog] = useState(false);
    const [technicalResponsiblesDialog, setTechnicalResponsiblesDialog] = useState(false);
    const [deleteResponsibleDialog, setDeleteResponsibleDialog] = useState(false);
    const [deleteTechnicalResponsibleDialog, setDeleteTechnicalResponsibleDialog] = useState(false);
    const [selectedTeacher, setSelectedTeacher] = useState(null);
    const [selectedTechnicalResponsible, setSelectedTechnicalResponsible] = useState(null);

    // License (year + issueDate)
    const [gymLicenseDialog, setGymLicenseDialog] = useState(false);
    const [editGymLicenseDialog, setEditGymLicenseDialog] = useState(false);
    const [deleteGymLicenseDialog, setDeleteGymLicenseDialog] = useState(false);
    const [licenseId, setLicenseId] = useState('');
    const [year, setYear] = useState('');
    const [issueDate, setIssueDate] = useState('');

    // Annuity (year + paymentDate)
    const [annuityDialog, setAnnuityDialog] = useState(false);
    const [editAnnuityDialog, setEditAnnuityDialog] = useState(false);
    const [deleteAnnuityDialog, setDeleteAnnuityDialog] = useState(false);
    const [annuityId, setAnnuityId] = useState('');
    const [annuityYear, setAnnuityYear] = useState('');
    const [annuityPaymentDate, setAnnuityPaymentDate] = useState('');

    // ==================== FETCH INICIAL ====================
    useEffect(() => {
        getStates().then(data => setStates(data));
        getTeachersSummary().then(data => {
            setTeachers(data);
            setGymResponsibles(data);
        });
        // filterByState nao carrega no mount - espera o dropdown
        if (fetchGyms && !filterByState) {
            fetchGyms().then(data => setGyms(data));
        }
    }, [fetchGyms, filterByState]);

    const handleFilterStateChange = (e) => {
        setSelectedFilterState(e.value);
        if (filterByState && e.value) {
            filterByState(e.value.id).then(data => setGyms(data));
        }
    };

    // ==================== HELPERS ====================
    function clearLicenseData() { setYear(''); setIssueDate(''); }
    function clearAnnuityData() { setAnnuityYear(''); setAnnuityPaymentDate(''); }
    const findIndexById = (id) => gyms.findIndex(g => g.id === id);
    const removeById = (arr, id) => {
        const i = arr.findIndex(el => el.id === id);
        return i >= 0 && !!arr.splice(i, 1);
    };

    // ==================== HANDLERS DE INPUT ====================
    const onInputChange = (e, name) => {
        const val = (e.target && e.target.value) || '';
        const _gym = { ...gym };
        if (name.includes('.')) {
            const [first, second] = name.split('.');
            _gym[first][second] = val;
        } else {
            _gym[name] = val;
        }
        setGym(_gym);
    };

    const setStreetNumber = (val) => {
        const _gym = { ...gym };
        _gym.gymAddress.streetNumber = val;
        setGym(_gym);
    };

    const onStateChange = (e) => {
        setSelectedState(e.value);
        setGym({ ...gym, state: e.value });
    };

    const onTeacherChange = (e) => setSelectedTeacher(e.value);
    const onTechnicalResponsibleChange = (e) => setSelectedTechnicalResponsible(e.value);

    // ==================== CRUD GYM ====================
    async function postGym(g) {
        const res = await apiFetch(properties.baseURL + properties.gymsPath, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(g),
        });
        if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            throw new Error(body.error || `Erro ${res.status} – ${res.statusText}`);
        }
        return res.json();
    }

    async function putGym(g) {
        const res = await apiFetch(properties.baseURL + properties.gymsPath + '/' + g.id, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(g),
        });
        if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            throw new Error(body.error || `Erro ${res.status} – ${res.statusText}`);
        }
        return res.json();
    }

    const openNew = () => {
        setGym(emptyGym);
        setSubmitted(false);
        setChecked(false);
        setSelectedState(null);
        setGymApiError('');
        setGymDialog(true);
    };

    const hideDialog = () => {
        setSubmitted(false);
        setGymApiError('');
        setGymDialog(false);
    };

    const saveGym = async () => {
        setSubmitted(true);
        setGymApiError('');

        if (!gym.name.trim()) return;

        // Remove máscara dos campos de telefone antes de enviar
        const contacts = { ...gym.contacts };
        ['mobile1', 'mobile2', 'phone1', 'phone2'].forEach(f => {
            if (contacts[f]) contacts[f] = stripMask(contacts[f]);
        });

        const _gym = { ...gym, contacts, affiliate: checked };

        try {
            if (gym.id) {
                await putGym(_gym);
                const _gyms = [...gyms];
                const index = findIndexById(gym.id);
                _gyms[index] = _gym;
                setGyms(_gyms);
                toast.current.show({ severity: 'success', summary: 'Successful', detail: 'Gym Updated', life: 3000 });
            } else {
                _gym.id = 0;
                _gym.image = 'product-placeholder.svg';
                await postGym(_gym);
                const _gyms = [...gyms];
                _gyms.push(_gym);
                setGyms(_gyms);
                toast.current.show({ severity: 'success', summary: 'Successful', detail: 'Gym Created', life: 3000 });
            }
            setGymDialog(false);
            setGym(emptyGym);
        } catch (err) {
            setGymApiError(err.message);
        }
    };

    const editGym = (g) => {
        // Converte campos E.164 para o formato do InputMask antes de armazenar no estado
        setGym({
            ...g,
            contacts: {
                ...g.contacts,
                mobile1: e164ToMasked(g.contacts.mobile1, 'mobile'),
                mobile2: e164ToMasked(g.contacts.mobile2, 'mobile'),
                phone1: e164ToMasked(g.contacts.phone1, 'phone'),
                phone2: e164ToMasked(g.contacts.phone2, 'phone'),
            },
        });
        setSelectedState({ id: g.state.id, name: g.state.name });
        setChecked(g.affiliate || false);
        setGymApiError('');
        setGymDialog(true);
    };

    // BUG FIX: era chamado `delGyms` ANTES da confirmacao
    const confirmDeleteGym = (g) => {
        setGym(g);
        setDeleteGymDialog(true);
    };

    const deleteGym = () => {
        const _gyms = gyms.filter(v => v.id !== gym.id);
        setGyms(_gyms);
        apiFetch(properties.baseURL + properties.gymsPath + '/' + gym.id, { method: 'DELETE' });
        setDeleteGymDialog(false);
        setGym(emptyGym);
        toast.current.show({ severity: 'success', summary: 'Successful', detail: 'Dojo deletado', life: 3000 });
    };

    const confirmDeleteSelected = () => setDeleteGymsDialog(true);
    const deleteSelectedGyms = () => {
        setGyms(gyms.filter(v => !selectedGyms.includes(v)));
        setDeleteGymsDialog(false);
        setSelectedGyms(null);
    };

    // ==================== RESPONSIBLES ====================
    const showResponsiblesDialog = () => setResponsiblesDialog(true);
    const showTechnicalResponsiblesDialog = () => setTechnicalResponsiblesDialog(true);
    const hideResponsiblesDialog = () => setResponsiblesDialog(false);
    const hideTechnicalResponsiblesDialog = () => setTechnicalResponsiblesDialog(false);
    const hideDeleteResponsibleDialog = () => setDeleteResponsibleDialog(false);
    const hideDeleteTechnicalResponsibleDialog = () => setDeleteTechnicalResponsibleDialog(false);

    const addResponsible = () => {
        if (!selectedTeacher) return;
        const _gym = { ...gym };
        const responsible = {
            id: selectedTeacher.id,
            name: selectedTeacher.name,
            grade: { level: selectedTeacher.grade?.level || selectedTeacher.level }
        };
        _gym.gymResponsibles = _gym.gymResponsibles.concat(responsible);
        setGym(_gym);
        toast.current.show({ severity: 'success', summary: 'Successful', detail: t('msg.jkaResponsibleAdd'), life: 3000 });
        setSelectedTeacher(null);
        setResponsiblesDialog(false);
    };

    const addTechnicalResponsible = () => {
        if (!selectedTechnicalResponsible) return;
        const _gym = { ...gym };
        const techResp = {
            id: selectedTechnicalResponsible.id,
            name: selectedTechnicalResponsible.name,
            grade: { level: selectedTechnicalResponsible.grade?.level || selectedTechnicalResponsible.level }
        };
        _gym.technicalResponsibles = _gym.technicalResponsibles.concat(techResp);
        setGym(_gym);
        toast.current.show({ severity: 'success', summary: 'Successful', detail: t('msg.jkaTechnicalResponsibleAdd'), life: 3000 });
        setSelectedTechnicalResponsible(null);
        setTechnicalResponsiblesDialog(false);
    };

    const confirmDeleteResponsible = (r) => {
        setIdToDelete(r.id);
        setDeleteResponsibleDialog(true);
    };

    const confirmDeleteTechnicalResponsible = (r) => {
        setIdToDelete(r.id);
        setDeleteTechnicalResponsibleDialog(true);
    };

    const deleteResponsible = () => {
        removeById(gym.gymResponsibles, idToDelete);
        // BUG FIX: era hardcoded http://localhost:8080
        apiFetch(`${properties.baseURL}/api/gyms/${gym.id}/responsible/${idToDelete}`, { method: 'DELETE' });
        setIdToDelete(0);
        setDeleteResponsibleDialog(false);
    };

    const deleteTechnicalResponsible = () => {
        removeById(gym.technicalResponsibles, idToDelete);
        // BUG FIX: era hardcoded http://localhost:8080
        apiFetch(`${properties.baseURL}/api/gyms/${gym.id}/technicalresponsible/${idToDelete}`, { method: 'DELETE' });
        setIdToDelete(0);
        setDeleteTechnicalResponsibleDialog(false);
    };

    // ==================== LICENSE ====================
    const showGymLicenseDialog = () => { clearLicenseData(); setGymLicenseDialog(true); };
    const hideInsertGymLicenseDialog = () => { clearLicenseData(); setGymLicenseDialog(false); };
    const hideEditGymLicenseDialog = () => setEditGymLicenseDialog(false);
    const hideDeleteGymLicenseDialog = () => setDeleteGymLicenseDialog(false);

    const addGymLicense = () => {
        const _gym = { ...gym };
        _gym.gymLicenseList = _gym.gymLicenseList.concat({ year, issueDate });
        setGym(_gym);
        toast.current.show({ severity: 'success', summary: 'Successful', detail: t('msg.jkaLicenseAdd'), life: 3000 });
        clearLicenseData();
        setGymLicenseDialog(false);
    };

    const editGymLicense = (lic) => {
        setLicenseId(lic.id);
        setYear(lic.year);
        setIssueDate(lic.issueDate);
        setEditGymLicenseDialog(true);
    };

    const updateGymLicense = () => {
        const _gym = { ...gym };
        const idx = _gym.gymLicenseList.findIndex(o => o.id === licenseId);
        _gym.gymLicenseList[idx].year = year;
        _gym.gymLicenseList[idx].issueDate = issueDate;
        setGym(_gym);
        clearLicenseData();
        setEditGymLicenseDialog(false);
    };

    const confirmDeleteGymLicense = (lic) => {
        setIdToDelete(lic.id);
        setDeleteGymLicenseDialog(true);
    };

    const deleteGymLicense = () => {
        const _gym = { ...gym };
        const idx = _gym.gymLicenseList.findIndex(o => o.id === idToDelete);
        delete _gym.gymLicenseList[idx];
        setGym(_gym);
        apiFetch(properties.baseURL + properties.gymLicensesPath + `/${idToDelete}`, { method: 'DELETE' });
        setIdToDelete(0);
        setDeleteGymLicenseDialog(false);
    };

    // ==================== ANNUITY ====================
    const showAnnuityDialog = () => { clearAnnuityData(); setAnnuityDialog(true); };
    const hideInsertAnnuityDialog = () => { clearAnnuityData(); setAnnuityDialog(false); };
    const hideEditAnnuityDialog = () => setEditAnnuityDialog(false);
    const hideDeleteAnnuityDialog = () => setDeleteAnnuityDialog(false);

    const addAnnuity = () => {
        const _gym = { ...gym };
        _gym.annuityList = _gym.annuityList.concat({ year: annuityYear, paymentDate: annuityPaymentDate });
        setGym(_gym);
        toast.current.show({ severity: 'success', summary: 'Successful', detail: t('msg.jkaAnnuityAdd'), life: 3000 });
        clearAnnuityData();
        setAnnuityDialog(false);
    };

    const editAnnuity = (a) => {
        setAnnuityId(a.id);
        setAnnuityYear(a.year);
        setAnnuityPaymentDate(a.paymentDate);
        setEditAnnuityDialog(true);
    };

    const updateAnnuity = () => {
        const _gym = { ...gym };
        const idx = _gym.annuityList.findIndex(o => o.id === annuityId);
        _gym.annuityList[idx].year = annuityYear;
        _gym.annuityList[idx].paymentDate = annuityPaymentDate;
        setGym(_gym);
        clearAnnuityData();
        setEditAnnuityDialog(false);
    };

    const confirmDeleteAnnuity = (a) => {
        setIdToDelete(a.id);
        setDeleteAnnuityDialog(true);
    };

    const deleteAnnuity = () => {
        const _gym = { ...gym };
        const idx = _gym.annuityList.findIndex(o => o.id === idToDelete);
        delete _gym.annuityList[idx];
        setGym(_gym);
        apiFetch(properties.baseURL + properties.gymAnnuitiesPath + `${idToDelete}`, { method: 'DELETE' });
        setIdToDelete(0);
        setDeleteAnnuityDialog(false);
    };

    // ==================== TABLE ====================
    const phoneBodyTemplate = (value) => {
        if (!value) return '';
        if (isValidE164(value)) return formatPhoneForDisplay(value);
        return (
            <span>
                {value}{' '}
                <i
                    className="pi pi-exclamation-triangle"
                    style={{ color: '#f59e0b' }}
                    title="Telefone não validado"
                />
            </span>
        );
    };

    const actionBody = (rowData) => (
        <>
            <Button icon="pi pi-pencil" className="p-button-rounded p-button-success mr-2" onClick={() => editGym(rowData)} />
            <Button icon="pi pi-trash" className="p-button-rounded p-button-danger" onClick={() => confirmDeleteGym(rowData)} />
        </>
    );

    const header = (
        <div className="flex flex-column md:flex-row md:align-items-center justify-content-between">
            <span className="p-input-icon-left w-full md:w-auto">
                <i className="pi pi-search" />
                <InputText type="search" onInput={(e) => setGlobalFilter(e.target.value)} placeholder="Search..." className="w-full lg:w-auto" />
            </span>
            <div className="mt-3 md:mt-0 flex justify-content-end">
                <Button icon="pi pi-plus" className="mr-2 p-button-rounded" onClick={openNew} tooltip="New" />
                <Button icon="pi pi-trash" className="p-button-danger mr-2 p-button-rounded" onClick={confirmDeleteSelected} disabled={!selectedGyms || !selectedGyms.length} tooltip="Delete" />
                <Button icon="pi pi-upload" className="p-button-help p-button-rounded" onClick={() => dt.current.exportCSV()} tooltip="Export" />
            </div>
        </div>
    );

    // ==================== FOOTERS DOS DIALOGS ====================
    const deleteGymDialogFooter = (
        <><Button label="No" icon="pi pi-times" className="p-button-text" onClick={() => setDeleteGymDialog(false)} />
        <Button label="Yes" icon="pi pi-check" className="p-button-text" onClick={deleteGym} /></>
    );
    const deleteGymsDialogFooter = (
        <><Button label="No" icon="pi pi-times" className="p-button-text" onClick={() => setDeleteGymsDialog(false)} />
        <Button label="Yes" icon="pi pi-check" className="p-button-text" onClick={deleteSelectedGyms} /></>
    );
    const insertResponsiblesFooter = (
        <><Button label="Nao" icon="pi pi-times" className="p-button-text" onClick={hideResponsiblesDialog} />
        <Button label="Sim" icon="pi pi-check" className="p-button-text" onClick={addResponsible} /></>
    );
    const insertTechnicalResponsiblesFooter = (
        <><Button label="Nao" icon="pi pi-times" className="p-button-text" onClick={hideTechnicalResponsiblesDialog} />
        <Button label="Sim" icon="pi pi-check" className="p-button-text" onClick={addTechnicalResponsible} /></>
    );
    const deleteResponsibleFooter = (
        <><Button label="No" icon="pi pi-times" className="p-button-text" onClick={hideDeleteResponsibleDialog} />
        <Button label="Yes" icon="pi pi-check" className="p-button-text" onClick={deleteResponsible} /></>
    );
    const deleteTechnicalResponsibleFooter = (
        <><Button label="No" icon="pi pi-times" className="p-button-text" onClick={hideDeleteTechnicalResponsibleDialog} />
        <Button label="Yes" icon="pi pi-check" className="p-button-text" onClick={deleteTechnicalResponsible} /></>
    );
    const insertGymLicenseFooter = (
        <><Button label="Nao" icon="pi pi-times" className="p-button-text" onClick={hideInsertGymLicenseDialog} />
        <Button label="Sim" icon="pi pi-check" className="p-button-text" onClick={addGymLicense} /></>
    );
    const editGymLicenseFooter = (
        <><Button label="Nao" icon="pi pi-times" className="p-button-text" onClick={hideEditGymLicenseDialog} />
        <Button label="Sim" icon="pi pi-check" className="p-button-text" onClick={updateGymLicense} /></>
    );
    const deleteGymLicenseFooter = (
        <><Button label="No" icon="pi pi-times" className="p-button-text" onClick={hideDeleteGymLicenseDialog} />
        <Button label="Yes" icon="pi pi-check" className="p-button-text" onClick={deleteGymLicense} /></>
    );
    const insertAnnuityFooter = (
        <><Button label="Nao" icon="pi pi-times" className="p-button-text" onClick={hideInsertAnnuityDialog} />
        <Button label="Sim" icon="pi pi-check" className="p-button-text" onClick={addAnnuity} /></>
    );
    const editAnnuityFooter = (
        <><Button label="Nao" icon="pi pi-times" className="p-button-text" onClick={hideEditAnnuityDialog} />
        <Button label="Sim" icon="pi pi-check" className="p-button-text" onClick={updateAnnuity} /></>
    );
    const deleteAnnuityFooter = (
        <><Button label="No" icon="pi pi-times" className="p-button-text" onClick={hideDeleteAnnuityDialog} />
        <Button label="Yes" icon="pi pi-check" className="p-button-text" onClick={deleteAnnuity} /></>
    );

    // ==================== RENDER ====================
    return (
        <div className="datatable-crud-demo surface-card p-4 border-round shadow-2">
            <Toast ref={toast} />

            <div className="text-3xl text-800 font-bold mb-4">
                {t(titleKey)}
                {filterByState && (
                    <span className="ml-3" style={{ verticalAlign: 'middle' }}>
                        <Dropdown value={selectedFilterState} options={states}
                            onChange={handleFilterStateChange} optionLabel="name"
                            placeholder="Selecione um estado"
                            style={{ fontSize: '1rem', fontWeight: 'normal' }} />
                    </span>
                )}
            </div>

            <DataTable ref={dt} value={gyms} selection={selectedGyms}
                onSelectionChange={(e) => setSelectedGyms(e.value)}
                dataKey="id" paginator rows={10} rowsPerPageOptions={[5, 10, 25]}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate={t('currentPageReportTemplateGyms')}
                globalFilter={globalFilter} header={header} responsiveLayout="scroll">
                <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} exportable={false} />
                <Column field="name" header={t('msg.jkaName')} sortable style={{ minWidth: '16rem' }} />
                <Column field="socialMedia.instagramId" header="Instagram" sortable style={{ minWidth: '5rem' }} />
                <Column field="socialMedia.facebookId" header="Facebook" sortable style={{ minWidth: '5rem' }} />
                <Column field="contacts.phone1" header="Telefone" sortable style={{ minWidth: '5rem' }}
                    body={(row) => phoneBodyTemplate(row.contacts?.phone1)} />
                <Column field="gymAddress.street" header="Logradouro" sortable style={{ minWidth: '16rem' }} />
                <Column field="gymAddress.streetNumber" header="Numero" sortable style={{ minWidth: '5rem' }} />
                <Column body={actionBody} exportable={false} style={{ minWidth: '8rem' }} />
            </DataTable>

            {/* Dialog principal DECOMPOSTO em tabs */}
            <GymFormDialog
                visible={gymDialog}
                onHide={hideDialog}
                onSave={saveGym}
                gym={gym}
                onInputChange={onInputChange}
                submitted={submitted}
                activeIndex={activeIndex}
                setActiveIndex={setActiveIndex}
                setStreetNumber={setStreetNumber}
                states={states}
                selectedState={selectedState}
                onStateChange={onStateChange}
                checked={checked}
                setChecked={setChecked}
                onAddResponsible={showResponsiblesDialog}
                onDeleteResponsible={confirmDeleteResponsible}
                onAddTechnical={showTechnicalResponsiblesDialog}
                onDeleteTechnical={confirmDeleteTechnicalResponsible}
                onAddLicense={showGymLicenseDialog}
                onEditLicense={editGymLicense}
                onDeleteLicense={confirmDeleteGymLicense}
                apiError={gymApiError}
                onAddAnnuity={showAnnuityDialog}
                onEditAnnuity={editAnnuity}
                onDeleteAnnuity={confirmDeleteAnnuity}
            />

            {/* ============ DIALOGS INTERNOS ============ */}

            {/* Responsible - insert */}
            <Dialog visible={responsiblesDialog} style={{ width: '650px' }} header="Inserir professores responsáveis pelo Dojo" modal className="p-fluid" footer={insertResponsiblesFooter} onHide={hideResponsiblesDialog}>
                <div className="field">
                    <Fieldset>
                        <div className="formgrid grid license">
                            <div className="field dropdown col-6">
                                <label htmlFor="teacher">{t('msg.jkaResponsible')}</label>
                                <Dropdown value={selectedTeacher} options={gymResponsibles} onChange={onTeacherChange} optionLabel="name" filter />
                            </div>
                        </div>
                    </Fieldset>
                </div>
            </Dialog>

            {/* Technical Responsible - insert */}
            <Dialog visible={technicalResponsiblesDialog} style={{ width: '650px' }} header="Responsável técnico pelo Dojo" modal className="p-fluid" footer={insertTechnicalResponsiblesFooter} onHide={hideTechnicalResponsiblesDialog}>
                <div className="field">
                    <Fieldset>
                        <div className="formgrid grid license">
                            <div className="field dropdown col-6">
                                <label htmlFor="techresp">{t('msg.jkaResponsible')}</label>
                                <Dropdown value={selectedTechnicalResponsible} options={teachers} onChange={onTechnicalResponsibleChange} optionLabel="name" filter />
                            </div>
                        </div>
                    </Fieldset>
                </div>
            </Dialog>

            {/* License - insert */}
            <Dialog visible={gymLicenseDialog} style={{ width: '650px' }} header="Inserir licença" modal className="p-fluid" footer={insertGymLicenseFooter} onHide={hideInsertGymLicenseDialog}>
                <div className="field">
                    <Fieldset>
                        <div className="formgrid grid license">
                            <div className="field input text col-3">
                                <label htmlFor="year">{t('msg.jkaYear')}</label>
                                <InputMask id="year" mask="9999" placeholder="2024" value={year} onChange={(e) => setYear(e.target.value)} />
                            </div>
                            <div className="field input text col-4">
                                <label htmlFor="licenseIssueDate">{t('msg.jkaLicenseIssueDate')}</label>
                                <InputMask id="licenseIssueDate" mask="99/99/9999" placeholder="31/12/2024" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
                            </div>
                        </div>
                    </Fieldset>
                </div>
            </Dialog>

            {/* License - edit */}
            <Dialog visible={editGymLicenseDialog} style={{ width: '720px' }} header={t('msg.jkaEditLicense')} modal className="p-fluid" footer={editGymLicenseFooter} onHide={hideEditGymLicenseDialog}>
                <div className="field">
                    <Fieldset>
                        <div className="formgrid grid license">
                            <div className="field input text col-3">
                                <label htmlFor="year">{t('msg.jkaYear')}</label>
                                <InputMask id="year" mask="9999" value={year} onChange={(e) => setYear(e.target.value)} />
                            </div>
                            <div className="field input text col-4">
                                <label htmlFor="licenseIssueDate">{t('msg.jkaLicenseIssueDate')}</label>
                                <InputText id="licenseIssueDate" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
                            </div>
                        </div>
                    </Fieldset>
                </div>
            </Dialog>

            {/* Annuity - insert */}
            <Dialog visible={annuityDialog} style={{ width: '650px' }} header="Pagamento de anuidade" modal className="p-fluid" footer={insertAnnuityFooter} onHide={hideInsertAnnuityDialog}>
                <div className="field">
                    <Fieldset>
                        <div className="formgrid grid license">
                            <div className="field input text col-3">
                                <label htmlFor="annuityYear">{t('msg.jkaYear')}</label>
                                <InputMask id="annuityYear" mask="9999" placeholder="2024" value={annuityYear} onChange={(e) => setAnnuityYear(e.target.value)} />
                            </div>
                            <div className="field input text col-4">
                                <label htmlFor="annuityPaymentDate">{t('msg.jkaAnnuityPaymentDate')}</label>
                                <InputMask id="annuityPaymentDate" mask="99/99/9999" placeholder="31/12/2024" value={annuityPaymentDate} onChange={(e) => setAnnuityPaymentDate(e.target.value)} />
                            </div>
                        </div>
                    </Fieldset>
                </div>
            </Dialog>

            {/* Annuity - edit */}
            <Dialog visible={editAnnuityDialog} style={{ width: '720px' }} header={t('msg.jkaEditAnnuity')} modal className="p-fluid" footer={editAnnuityFooter} onHide={hideEditAnnuityDialog}>
                <div className="field">
                    <Fieldset>
                        <div className="formgrid grid license">
                            <div className="field input text col-3">
                                <label htmlFor="annuityYear">{t('msg.jkaYear')}</label>
                                <InputMask id="annuityYear" mask="9999" value={annuityYear} onChange={(e) => setAnnuityYear(e.target.value)} />
                            </div>
                            <div className="field input text col-4">
                                <label htmlFor="annuityPaymentDate">{t('msg.jkaAnnuityPaymentDate')}</label>
                                <InputMask id="annuityPaymentDate" mask="99/99/9999" value={annuityPaymentDate} onChange={(e) => setAnnuityPaymentDate(e.target.value)} />
                            </div>
                        </div>
                    </Fieldset>
                </div>
            </Dialog>

            {/* ============ DELETE CONFIRMATIONS ============ */}
            <Dialog visible={deleteGymDialog} style={{ width: '450px' }} header="Confirm" modal footer={deleteGymDialogFooter} onHide={() => setDeleteGymDialog(false)}>
                <div className="flex align-items-center justify-content-center">
                    <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                    {gym && <span>Are you sure you want to delete <b>{gym.name}</b>?</span>}
                </div>
            </Dialog>
            <Dialog visible={deleteGymsDialog} style={{ width: '450px' }} header="Confirm" modal footer={deleteGymsDialogFooter} onHide={() => setDeleteGymsDialog(false)}>
                <div className="flex align-items-center justify-content-center">
                    <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                    <span>Are you sure you want to delete the selected gyms?</span>
                </div>
            </Dialog>
            <Dialog visible={deleteResponsibleDialog} style={{ width: '450px' }} header="Confirm" modal footer={deleteResponsibleFooter} onHide={hideDeleteResponsibleDialog}>
                <div className="flex align-items-center justify-content-center">
                    <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                    <span>Are you sure you want to delete this responsible?</span>
                </div>
            </Dialog>
            <Dialog visible={deleteTechnicalResponsibleDialog} style={{ width: '450px' }} header="Confirm" modal footer={deleteTechnicalResponsibleFooter} onHide={hideDeleteTechnicalResponsibleDialog}>
                <div className="flex align-items-center justify-content-center">
                    <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                    <span>Are you sure you want to delete this technical responsible?</span>
                </div>
            </Dialog>
            <Dialog visible={deleteGymLicenseDialog} style={{ width: '450px' }} header="Confirm" modal footer={deleteGymLicenseFooter} onHide={hideDeleteGymLicenseDialog}>
                <div className="flex align-items-center justify-content-center">
                    <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                    <span>Are you sure you want to delete this license?</span>
                </div>
            </Dialog>
            <Dialog visible={deleteAnnuityDialog} style={{ width: '450px' }} header="Confirm" modal footer={deleteAnnuityFooter} onHide={hideDeleteAnnuityDialog}>
                <div className="flex align-items-center justify-content-center">
                    <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                    <span>Are you sure you want to delete this annuity?</span>
                </div>
            </Dialog>
        </div>
    );
}

export default GymList;
